<script setup lang="ts">
import type { Database } from '#shared/types/database.types'
import AuthProvider from '@/components/AuthProvider.vue'
import { logApiError } from '~/utils/logger'

const props = withDefaults(defineProps<{ magicLinkRedirect?: string }>(), {
  magicLinkRedirect: undefined,
})

const emit = defineEmits<{
  loginSuccess: []
}>()

const authStore = useAuthStore()
const { signInWithMagicLink } = useSupabaseAuth()
const { userId } = useUser()

const loading = ref(false)

// Embedded login form state
const loginCredentials = ref({
  email: '',
  password: '',
})

const loginErrors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
})

const isLoginSubmitting = ref(false)
const isLoginPasswordVisible = ref(false)

// Magic link state
const isMagicLinkSubmitting = ref(false)
const magicLinkMessage = ref<string | null>(null)
const magicLinkError = ref<string | null>(null)

// Toggle between login and register mode
const authMode = ref<'login' | 'register'>('login')

const registerPassword = ref('')
const registerPasswordConfirm = ref('')
const acceptTerms = ref(false)
const registerError = ref<string | null>(null)
const registerSubmitting = ref(false)

const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

const personalInfo = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  rijksregisternummer: '',
  address: '',
  postalCode: '',
  city: '',
  country: 'België',
})

// Format rijksregisternummer as user types
const formatRijksregisternummer = (value: string) => {
  if (!value)
    return ''

  const digits = value.replace(/\D/g, '')

  let formatted = ''
  if (digits.length > 0)
    formatted += digits.substring(0, 2)
  if (digits.length > 2)
    formatted += `.${digits.substring(2, 4)}`
  if (digits.length > 4)
    formatted += `.${digits.substring(4, 6)}`
  if (digits.length > 6)
    formatted += `-${digits.substring(6, 9)}`
  if (digits.length > 9)
    formatted += `.${digits.substring(9, 11)}`

  return formatted
}

// Fetch user profile from database and fill in the form
const fetchAndFillUserProfile = async () => {
  if (!userId.value)
    return

  if (loading.value)
    return

  loading.value = true
  try {
    const response = await $fetch<{ success: boolean, data: Database['public']['Tables']['vzw_user_profiles']['Row'] | null }>(
      '/api/user/profile',
      {
        method: 'GET',
      },
    )

    if (response.success && response.data) {
      const data = response.data

      const nameParts = (data?.full_name || '').split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      // Use new separate fields if available, otherwise fallback to parsing from combined address
      let address = data?.address || ''
      let postalCode = data?.postal_code || ''
      let city = data?.city || ''

      // Backward compatibility: parse from combined address if new fields are empty
      if (!postalCode && !city && address) {
        const trimmedAddress = address.trim()
        const postalCityMatch = trimmedAddress.match(/(\d{4})\s+(\S.*)$/)

        if (postalCityMatch && typeof postalCityMatch.index === 'number') {
          postalCode = postalCityMatch[1] ?? ''
          city = postalCityMatch[2]?.trim() ?? ''

          const addressPart = trimmedAddress
            .slice(0, postalCityMatch.index)
            .replace(/,\s*$/, '')
            .trim()

          address = addressPart
        }
      }

      personalInfo.value = {
        firstName,
        lastName,
        email: authStore.user?.email || '',
        phone: data?.phone || '',
        rijksregisternummer: formatRijksregisternummer(data?.rijksregisternummer || ''),
        address,
        postalCode,
        city,
        country: data?.country || 'België',
      }
    }
    else {
      personalInfo.value.email = authStore.user?.email || ''
    }
  }
  catch (error) {
    logApiError('/api/user/profile', error, { operation: 'fetchAndFillUserProfile' })

    if (authStore.user?.email)
      personalInfo.value.email = authStore.user?.email || ''
  }
  finally {
    loading.value = false
  }
}

// Save profile data to database
const saveProfileData = async () => {
  if (!authStore.user)
    return

  try {
    // Only send rijksregisternummer if it's properly formatted (11 digits)
    const rrnDigits = personalInfo.value.rijksregisternummer.replace(/\D/g, '')
    const formattedRrn = rrnDigits.length === 11 ? personalInfo.value.rijksregisternummer : null

    await $fetch('/api/user/profile', {
      method: 'PATCH',
      body: {
        full_name: `${personalInfo.value.firstName} ${personalInfo.value.lastName}`.trim(),
        phone: personalInfo.value.phone,
        rijksregisternummer: formattedRrn,
        address: personalInfo.value.address,
        postal_code: personalInfo.value.postalCode,
        city: personalInfo.value.city,
        country: personalInfo.value.country,
      },
    })
  }
  catch (error) {
    logApiError('/api/user/profile', error, { operation: 'saveProfileData' })
  }
}

// Validation: all personal info fields must be filled
const isAddressComplete = computed(() => {
  return !!(
    personalInfo.value.firstName?.trim()
    && personalInfo.value.lastName?.trim()
    && personalInfo.value.phone?.trim()
    && personalInfo.value.address?.trim()
    && personalInfo.value.postalCode?.trim()
    && personalInfo.value.city?.trim()
  )
})

// Expose saveProfileData for parent component to call
defineExpose({
  saveProfileData,
  isAddressComplete,
})

// Handle embedded login
const handleEmbeddedLogin = async () => {
  if (isLoginSubmitting.value)
    return

  isLoginSubmitting.value = true

  try {
    loginErrors.value = {}

    if (!loginCredentials.value.email) {
      loginErrors.value.email = 'E-mailadres is verplicht'

      return
    }

    if (!emailRegex.test(loginCredentials.value.email)) {
      loginErrors.value.email = 'Ongeldig e-mailadres'

      return
    }

    if (!loginCredentials.value.password) {
      loginErrors.value.password = 'Wachtwoord is verplicht'

      return
    }

    const result = await authStore.login(
      loginCredentials.value.email,
      loginCredentials.value.password,
    )

    if (result.error) {
      if (result.error.message?.includes('Invalid login credentials')) {
        loginErrors.value.password = 'Onjuist e-mailadres of wachtwoord'
      }
      else {
        loginErrors.value.password = result.error.message || 'Inloggen mislukt'
      }

      return
    }

    await new Promise(resolve => setTimeout(resolve, 500))
    await fetchAndFillUserProfile()

    emit('loginSuccess')
  }
  finally {
    isLoginSubmitting.value = false
  }
}

// Handle magic link login (passwordless)
const handleMagicLink = async () => {
  if (isMagicLinkSubmitting.value)
    return

  magicLinkMessage.value = null
  magicLinkError.value = null

  const email = loginCredentials.value.email?.trim()

  if (!email) {
    magicLinkError.value = 'E-mailadres is verplicht'

    return
  }

  if (!emailRegex.test(email)) {
    magicLinkError.value = 'Vul een geldig e-mailadres in.'

    return
  }

  isMagicLinkSubmitting.value = true

  try {
    const { error } = await signInWithMagicLink(email, props.magicLinkRedirect)

    if (error) {
      if ('statusCode' in error && error.statusCode === 429) {
        const retryMinutes = error.retryAfter ? Math.ceil(error.retryAfter / 60000) : 60

        magicLinkError.value = `Te veel magic link verzoeken. Probeer het opnieuw over ${retryMinutes} ${retryMinutes !== 1 ? 'minuten' : 'minuut'}.`

        return
      }

      magicLinkError.value = error.message || 'Magic link versturen mislukt.'

      return
    }

    magicLinkMessage.value = 'Magic link verstuurd! Controleer je inbox en klik op de link om in te loggen.'
  }
  finally {
    isMagicLinkSubmitting.value = false
  }
}

// Register and login
const registerAndLogin = async () => {
  registerSubmitting.value = true
  registerError.value = null

  try {
    if (!personalInfo.value.email || !emailRegex.test(personalInfo.value.email)) {
      registerError.value = 'Vul een geldig e-mailadres in'

      return false
    }

    if (!personalInfo.value.firstName || !personalInfo.value.lastName) {
      registerError.value = 'Vul je voor- en achternaam in'

      return false
    }

    if (!personalInfo.value.address?.trim() || !personalInfo.value.postalCode?.trim() || !personalInfo.value.city?.trim()) {
      registerError.value = 'Vul je adresgegevens volledig in (adres, postcode en stad)'

      return false
    }

    if (!registerPassword.value || registerPassword.value.length < 8) {
      registerError.value = 'Wachtwoord moet minstens 8 tekens bevatten'

      return false
    }

    if (registerPassword.value !== registerPasswordConfirm.value) {
      registerError.value = 'Wachtwoorden komen niet overeen'

      return false
    }

    if (!acceptTerms.value) {
      registerError.value = 'Je moet de algemene voorwaarden accepteren'

      return false
    }

    await $fetch('/api/auth/register-checkout', {
      method: 'POST',
      body: {
        email: personalInfo.value.email,
        password: registerPassword.value,
        full_name: `${personalInfo.value.firstName} ${personalInfo.value.lastName}`.trim(),
        phone: personalInfo.value.phone || null,
        address: personalInfo.value.address || null,
        postal_code: personalInfo.value.postalCode || null,
        city: personalInfo.value.city || null,
        country: personalInfo.value.country || null,
      },
    })

    const loginResult = await authStore.login(
      personalInfo.value.email,
      registerPassword.value,
    )

    if (loginResult.error) {
      registerError.value = loginResult.error.message || 'Inloggen mislukt na registratie.'

      return false
    }

    await new Promise(resolve => setTimeout(resolve, 200))
    await saveProfileData()

    emit('loginSuccess')

    return true
  }
  catch (error) {
    const err = error as { data?: { message?: string }, statusMessage?: string, message?: string }

    logApiError('/api/auth/register-checkout', error, { operation: 'registerAndLogin' })
    registerError.value = err.data?.message || err.statusMessage || err.message || 'Registratie mislukt.'

    return false
  }
  finally {
    registerSubmitting.value = false
  }
}

// Watch for auth state changes
watch(() => authStore.isAuthenticated, async isAuthenticated => {
  if (isAuthenticated)
    await fetchAndFillUserProfile()
}, { immediate: true })
</script>

<template>
  <div class="cart2-personal-info">
    <!-- Logged In State -->
    <VCard v-if="authStore.isAuthenticated" variant="outlined" class="mb-4">
      <VCardText>
        <div class="d-flex align-center gap-2 mb-4">
          <VIcon icon="bx-user-check" size="24" color="success" />
          <h6 class="text-h6 mb-0">
            Jouw gegevens
          </h6>
          <VChip color="success" size="small" class="ms-auto">
            <VIcon icon="bx-check" start size="16" />
            Ingelogd
          </VChip>
        </div>

        <VRow v-if="loading">
          <VCol cols="12" class="text-center py-6">
            <VProgressCircular indeterminate color="primary" />
          </VCol>
        </VRow>

        <VRow v-else>
          <VCol cols="12" md="6">
            <VTextField
              v-model="personalInfo.firstName"
              label="Voornaam *"
              :rules="[v => !!v || 'Voornaam is verplicht'] "
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="personalInfo.lastName"
              label="Achternaam *"
              :rules="[v => !!v || 'Achternaam is verplicht']"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="personalInfo.email"
              label="E-mailadres"
              type="email"
              hide-details="auto"
              disabled
              hint="E-mailadres kan niet worden gewijzigd"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="personalInfo.phone"
              label="Telefoonnummer *"
              :rules="[v => !!v || 'Telefoonnummer is verplicht']"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="12">
            <VTextField
              v-model="personalInfo.address"
              label="Straat en huisnummer *"
              :rules="[v => !!v || 'Straat en huisnummer is verplicht']"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="6" md="6">
            <VTextField
              v-model="personalInfo.postalCode"
              label="Postcode *"
              :rules="[v => !!v || 'Postcode is verplicht']"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="6" md="6">
            <VTextField
              v-model="personalInfo.city"
              label="Gemeente *"
              :rules="[v => !!v || 'Gemeente is verplicht']"
              hide-details="auto"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Login Form (when not logged in and in login mode) -->
    <VCard v-else-if="authMode === 'login'" variant="text" class="mb-4">
      <VCardText class="cart2-auth-form">
        <div class="d-flex align-center gap-2 mb-4">
          <VIcon icon="bx-log-in" size="24" color="primary" />
          <h6 class="text-h6 mb-0">
            Inloggen
          </h6>
        </div>
        <p class="text-body-2 mb-4">
          Log in om je gegevens automatisch in te vullen en je boeking te voltooien.
        </p>

        <VTextField
          v-model="loginCredentials.email"
          label="E-mailadres"
          type="email"
          :error-messages="loginErrors.email"
          hide-details="auto"
          class="mb-3"
        />

        <VTextField
          v-model="loginCredentials.password"
          label="Wachtwoord"
          :type="isLoginPasswordVisible ? 'text' : 'password'"
          autocomplete="current-password"
          :error-messages="loginErrors.password"
          :append-inner-icon="isLoginPasswordVisible ? 'bx-hide' : 'bx-show'"
          hide-details="auto"
          class="mb-3"
          @click:append-inner="isLoginPasswordVisible = !isLoginPasswordVisible"
          @keyup.enter="handleEmbeddedLogin"
        />

        <div class="d-flex align-center flex-wrap justify-space-between gap-2 mb-4">
          <NuxtLink
            class="text-primary text-sm"
            :to="{ name: 'auth-forgot-password' }"
            target="_blank"
          >
            Wachtwoord vergeten?
          </NuxtLink>
        </div>

        <VBtn
          block
          color="primary"
          :loading="isLoginSubmitting"
          class="mb-4"
          @click="handleEmbeddedLogin"
        >
          <VIcon icon="bx-log-in" start />
          Inloggen
        </VBtn>

        <VAlert v-if="magicLinkError" type="error" variant="tonal" class="mb-3">
          {{ magicLinkError }}
        </VAlert>

        <VAlert v-else-if="magicLinkMessage" type="success" variant="tonal" class="mb-3">
          {{ magicLinkMessage }}
        </VAlert>

        <VDivider class="my-4" />

        <div class="d-flex align-center justify-center gap-2 flex-wrap mb-4">
          <span class="text-body-2">Of log in met:</span>
          <AuthProvider
            :magic-link-loading="isMagicLinkSubmitting"
            :magic-link-disabled="!loginCredentials.email"
            @magic-link-click="handleMagicLink"
          />
        </div>

        <VDivider class="my-4" />

        <div class="text-center">
          <span class="text-body-2">Nog geen account?</span>
          <VBtn
            variant="text"
            color="primary"
            size="small"
            @click="authMode = 'register'"
          >
            Registreren
          </VBtn>
        </div>
      </VCardText>
    </VCard>

    <!-- Register Form (when not logged in and in register mode) -->
    <VCard v-else variant="outlined" class="mb-4">
      <VCardText class="cart2-auth-form">
        <div class="d-flex align-center gap-2 mb-4">
          <VIcon icon="bx-user-plus" size="24" color="primary" />
          <h6 class="text-h6 mb-0">
            Account aanmaken
          </h6>
        </div>
        <p class="text-body-2 mb-4">
          Maak een account aan om je boeking te voltooien en je gegevens op te slaan voor volgende boekingen.
        </p>

        <VAlert v-if="registerError" type="error" variant="tonal" class="mb-4">
          {{ registerError }}
        </VAlert>

        <VRow>
          <VCol cols="12" md="6">
            <VTextField
              v-model="personalInfo.firstName"
              label="Voornaam *"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="personalInfo.lastName"
              label="Achternaam *"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12">
            <VTextField
              v-model="personalInfo.email"
              label="E-mailadres *"
              type="email"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12">
            <VTextField
              v-model="personalInfo.phone"
              label="Telefoonnummer *"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12">
            <VTextField
              v-model="personalInfo.address"
              label="Adres *"
              placeholder="Straat en huisnummer"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="personalInfo.postalCode"
              label="Postcode *"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="personalInfo.city"
              label="Stad *"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect
              v-model="personalInfo.country"
              label="Land *"
              :items="['Belgi\u00eb', 'Nederland', 'Duitsland', 'Frankrijk', 'Luxemburg']"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="registerPassword"
              label="Wachtwoord *"
              type="password"
              autocomplete="new-password"
              hide-details="auto"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="registerPasswordConfirm"
              label="Bevestig wachtwoord *"
              type="password"
              autocomplete="new-password"
              hide-details="auto"
            />
          </VCol>
        </VRow>

        <VCheckbox
          v-model="acceptTerms"
          class="mb-4"
        >
          <template #label>
            <span class="text-body-2">
              Ik ga akkoord met de
              <NuxtLink to="/legal/terms" target="_blank" class="text-primary">
                algemene voorwaarden
              </NuxtLink>
              en
              <NuxtLink to="/legal/privacy" target="_blank" class="text-primary">
                privacybeleid
              </NuxtLink>
            </span>
          </template>
        </VCheckbox>

        <VBtn
          block
          color="primary"
          :loading="registerSubmitting"
          class="mb-4"
          @click="registerAndLogin"
        >
          <VIcon icon="bx-user-plus" start />
          Account aanmaken
        </VBtn>

        <VDivider class="my-4" />

        <div class="text-center">
          <span class="text-body-2">Al een account?</span>
          <VBtn
            variant="text"
            color="primary"
            size="small"
            @click="authMode = 'login'"
          >
            Inloggen
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
@media (max-width: 960px) {
  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-personal-info :deep(.v-row) {
    margin-block: 0;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-personal-info :deep(.v-col) {
    padding-block: 18px;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-personal-info :deep(.v-input) {
    margin-block-end:20px;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-auth-form :deep(.v-row) {
    margin-block: 0;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-auth-form :deep(.v-col) {
    padding-block: 4px;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-auth-form :deep(.v-input) {
    margin-block-end: 18px;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-auth-form :deep(.v-field__details) {
    min-block-size: 0;
    padding-block-start: 2px;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-auth-form :deep(.mb-3) {
    margin-block-end: 8px !important;
  }

  /* stylelint-disable-next-line selector-pseudo-class-no-unknown */
  .cart2-auth-form :deep(.mb-4) {
    margin-block-end: 12px !important;
  }
}
</style>
